import { writable, get } from 'svelte/store';
import { jwtDecode } from 'jwt-decode';
import site from './site';

// Types
export interface TokenData {
	lastName: string;
	firstName: string;
	userUUID: string;
	username: string;
	email: string;
	isActive: boolean;
	roles: { name: string; roleUUID: string }[];
	iat: number;
	exp: number;
}

export interface UserProfile {
	lastName: string;
	firstName: string;
	userUUID: string;
	email: string;
	profilePictureURL?: string;
}

// Storable - writable store with localStorage persistence (plain string storage)
function storable<T extends string>(key: string, initialValue: T) {
	const stored = localStorage.getItem(key);
	const initial = (stored ?? initialValue) as T;
	const store = writable<T>(initial);

	store.subscribe((value) => {
		if (value === null || value === undefined) {
			localStorage.removeItem(key);
		} else {
			localStorage.setItem(key, value);
		}
	});

	return store;
}

// Stores
export const isAuthorized = writable(false);
export const isAuthorizationPending = writable(true);
export const colorTheme = storable<'dark' | 'light'>('color-theme', 'dark');
export const userProfileAvailable = writable(false);

// Token storage key
const TOKEN_KEY = 'jwt2';

// AbstractID module
const AbstractID = {
	tokenData: null as TokenData | null,
	userProfilePictureURL: '' as string,
	refreshInterval: null as ReturnType<typeof setInterval> | null,

	get isAdministrator(): boolean {
		if (!this.tokenData) return false;
		if (!site.adminRole || site.adminRole.length === 0) return false;
		return this.tokenData.roles?.some(r => r.name === site.adminRole) ?? false;
	},

	get isUserApplicationAccessRoleAvailable(): boolean {
		if (!site.accessRole || site.accessRole.length === 0) return true;
		return this.tokenData?.roles?.some(r => r.name === site.accessRole) ?? false;
	},

	getToken(): string | null {
		return localStorage.getItem(TOKEN_KEY);
	},

	setToken(tokenStringValue: string): void {
		try {
			this.tokenData = jwtDecode<TokenData>(tokenStringValue);
			localStorage.setItem(TOKEN_KEY, tokenStringValue);

			// Fetch profile picture if not already available
			if (!get(userProfileAvailable)) {
				fetch(`${site.backend.idBaseURL}/user-backend/user/${this.tokenData!.userUUID}/profile`)
					.then((response) => response.json())
					.then((profileJson) => {
						if (profileJson.data?.profilePictureURL?.length > 0) {
							this.userProfilePictureURL = profileJson.data.profilePictureURL;
							userProfileAvailable.set(true);
						}
					})
					.catch(() => {});
			}
		} catch {
			this.tokenData = null;
			localStorage.removeItem(TOKEN_KEY);
		}
	},

	clearToken(): void {
		localStorage.removeItem(TOKEN_KEY);
		this.tokenData = null;
	},

	navigateToLogin(redirectURL?: string): void {
		let verificationURL = redirectURL ?? document.location.href;
		// Strip query params for verification URL
		verificationURL = verificationURL.substring(0, verificationURL.indexOf("?")) || verificationURL;
		window.location.href = `${site.idBaseURL}/login?verification_url=${encodeURIComponent(verificationURL)}&app=${site.appID}&returnURL=${encodeURIComponent(site.baseURL)}`;
	},

	refreshToken(onError?: (error: { status: number; statusText: string }) => void): void {
		const tokenString = this.getToken();
		if (!tokenString) return;

		const xhr = new XMLHttpRequest();
		xhr.open("POST", `${site.backend.idBaseURL}/user-backend/auth/refresh`);
		xhr.setRequestHeader('Content-type', 'application/json');
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				const newToken = JSON.parse(xhr.response);
				if (newToken?.data?.accessToken?.length > 0) {
					this.setToken(newToken.data.accessToken);
				}
			} else {
				if (onError) onError({ status: xhr.status, statusText: xhr.statusText });
			}
		};
		xhr.onerror = () => {
			if (onError) onError({ status: xhr.status, statusText: xhr.statusText });
		};
		xhr.send(JSON.stringify({ token: tokenString }));
	},

	initialize(options: { redirectIfUnauthenticated?: boolean } = {}): void {
		const redirectIfUnauthenticated = options.redirectIfUnauthenticated ?? true;

		// Apply persisted theme early so the public (anonymous) shell is themed too
		const savedThemeEarly = localStorage.getItem('color-theme');
		if (!savedThemeEarly) {
			colorTheme.set('dark');
		}
		document.documentElement.classList.toggle('dark', get(colorTheme) === 'dark');

		// Check for token in URL (callback from AbstractID)
		const searchParams = new URLSearchParams(window.location.search);
		const tokenStringValue = searchParams.get('token');
		if (tokenStringValue && tokenStringValue.length > 0) {
			this.setToken(tokenStringValue);
			// Clean URL - remove query string but keep hash
			window.history.replaceState({}, '', window.location.pathname + window.location.hash);
		}

		const initialToken = this.getToken();

		const fnHandleNullToken = () => {
			this.clearToken();
			if (redirectIfUnauthenticated) {
				this.navigateToLogin();
			} else {
				isAuthorized.set(false);
				isAuthorizationPending.set(false);
			}
			return true;
		};

		if (!initialToken) {
			if (!this.tokenData) {
				fnHandleNullToken();
				return;
			}
			isAuthorized.set(true);
			isAuthorizationPending.set(false);
		} else {
			this.setToken(initialToken);

			if (!this.tokenData) {
				fnHandleNullToken();
				return;
			}

			// Check expiration
			const expirationDate = new Date(0);
			expirationDate.setUTCSeconds(this.tokenData.exp);

			if (expirationDate < new Date()) {
				fnHandleNullToken();
				return;
			}

			// Set up automatic token refresh
			const tokenRefreshIntervalInSeconds = 10;
			const fnCancelAutomaticTokenRefresh = () => {
				if (this.refreshInterval) {
					clearInterval(this.refreshInterval);
					this.refreshInterval = null;
				}
			};

			const onTokenError = () => {
				fnCancelAutomaticTokenRefresh();
				fnHandleNullToken();
			};

			fnCancelAutomaticTokenRefresh();
			this.refreshInterval = setInterval(() => {
				this.refreshToken(onTokenError);
			}, tokenRefreshIntervalInSeconds * 1000);
			this.refreshToken(onTokenError);

			isAuthorized.set(true);
			isAuthorizationPending.set(false);
		}

		// Apply persisted theme
		const savedTheme = localStorage.getItem('color-theme');
		if (!savedTheme) {
			colorTheme.set('dark');
		}
		document.documentElement.classList.toggle('dark', get(colorTheme) === 'dark');
	},

	logout(): void {
		if (this.refreshInterval) {
			clearInterval(this.refreshInterval);
			this.refreshInterval = null;
		}
		this.clearToken();
		isAuthorized.set(false);
		this.navigateToLogin();
	},

	openManagementPortal(): void {
		window.open(`${site.idBaseURL}/manage`, '_blank');
	},

	// Authenticated fetch helpers
	authenticatedFetch(url: string): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}
		return fetch(url, {
			headers: {
				'X-AbstractStatus-UserToken': token,
			},
			signal: AbortSignal.timeout(30_000)
		});
	},

	authenticatedPost(url: string, contentType: string | undefined, body: BodyInit): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}

		const headers: Record<string, string> = {
			'X-AbstractStatus-UserToken': token,
		};

		if (contentType) {
			headers['Content-Type'] = contentType;
		}

		return fetch(url, {
			method: 'POST',
			headers,
			body,
			signal: AbortSignal.timeout(30_000)
		});
	},

	authenticatedPut(url: string, contentType: string, body: BodyInit): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}

		const headers: Record<string, string> = {
			'X-AbstractStatus-UserToken': token,
		};

		if (contentType) {
			headers['Content-Type'] = contentType;
		}

		return fetch(url, {
			method: 'PUT',
			headers,
			body,
			signal: AbortSignal.timeout(30_000)
		});
	},

	authenticatedDelete(url: string): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}
		return fetch(url, {
			method: 'DELETE',
			headers: {
				'X-AbstractStatus-UserToken': token,
			},
			signal: AbortSignal.timeout(30_000)
		});
	}
};

export default AbstractID;
